import { useEffect, useRef, useState } from "react";
import { CALENDAR_DAY_EVENT_MAX_WIDTH_PX } from "../const/calendarDay";
import { ICalendarDayEventColumnsLayout } from "../helpers/calendarDayEventLayout";

interface IEventPosition {
    left: number;
    width: number;
    zIndex: number;
}

export const useCalendarDayEventsLayer = (
    formatGroupCount: (count: number) => string,
) => {
    const eventsLayerRef = useRef<HTMLLIElement | null>(null)
    const [eventsLayerWidth, setEventsLayerWidth] = useState<number>(
        CALENDAR_DAY_EVENT_MAX_WIDTH_PX
    )

    useEffect(() => {
        const eventsLayer = eventsLayerRef.current

        if(!eventsLayer) return

        const updateWidth = () => setEventsLayerWidth(eventsLayer.clientWidth)
        const resizeObserver = new ResizeObserver(updateWidth)

        updateWidth()
        resizeObserver.observe(eventsLayer)

        return () => resizeObserver.disconnect()
    }, [])

    const getEventPosition = (
        columnsLayout: ICalendarDayEventColumnsLayout,
        isGroup: boolean,
    ): IEventPosition => {
        const {
            eventCount,
            groupCount,
            eventsBefore,
            groupsBefore,
            maxGroupedEventCount,
        } = columnsLayout
        const itemCount = eventCount + groupCount
        const gap = itemCount > 1 ? 4 : 0
        const availableWidth = Math.max(
            eventsLayerWidth - gap * (itemCount - 1),
            itemCount,
        )
        const widthUnit = availableWidth / (eventCount * 3 + groupCount)
        const groupLabel = formatGroupCount(maxGroupedEventCount)
        const groupLabelWidth = 22 + groupLabel.length * 6.5
        const groupWidth = groupCount ? Math.min(92, groupLabelWidth, widthUnit) : 0
        const eventWidth = eventCount
            ? Math.min(
                CALENDAR_DAY_EVENT_MAX_WIDTH_PX,
                Math.max(1, (availableWidth - groupWidth * groupCount) / eventCount),
            )
            : 0
        const itemsBefore = eventsBefore + groupsBefore

        return {
            left: eventsBefore * eventWidth + groupsBefore * groupWidth + itemsBefore * gap,
            width: isGroup ? groupWidth : eventWidth,
            zIndex: itemsBefore + 1,
        }
    }

    return {
        eventsLayerRef,
        eventsLayerWidth,
        getEventPosition,
    }
}
